/**
 * Wishlist toggle — binds the theme's own heart buttons on product cards to the
 * Swish (Wishlist King / appmate) JS API, so clicking a heart adds/removes the
 * product from the wishlist without using Swish's auto-injected button.
 *
 * Buttons opt in with `data-wishlist-toggle` and carry:
 *   data-product-id, data-product-handle, data-variant-id
 *
 * Selected state is reflected via `aria-pressed="true"` and a `.is-wishlisted`
 * class on the button (the heart fills in CSS). The header "Favourites (N)"
 * badge is kept in sync too.
 *
 * State comes from a single subscription to Swish's reactive wishlist observable
 * (`state.observeWishlist`) — the same source Swish's own components use. This is
 * the single source of truth: every emission (our own toggles, the wishlist
 * page, another tab, a login merge) re-syncs every button and the counter. We
 * never poll or read a cached snapshot, so there are no stale-state races.
 */
class WishlistController {
  /** @type {WishlistController | null} */
  static #instance = null;

  static get() {
    return (this.#instance ??= new WishlistController());
  }

  /** productId (string) -> wishlistItemId, mirrored from the live wishlist. */
  #itemsByProductId = new Map();
  /** @type {Set<WishlistToggle>} */
  #buttons = new Set();
  /** Resolves once the first wishlist emission has been applied. */
  #ready = false;
  /** Last known item count, so header badges that mount late still get it. */
  #lastCount = 0;

  constructor() {
    this.#whenSwishReady().then((wk) => this.#subscribe(wk));
  }

  /** Resolves with window.WishlistKing once the app script has booted. */
  #whenSwishReady() {
    return new Promise((resolve) => {
      const ready = () => window.WishlistKing?.state?.observeWishlist && window.WishlistKing;
      if (ready()) return resolve(window.WishlistKing);
      const id = setInterval(() => {
        if (ready()) {
          clearInterval(id);
          resolve(window.WishlistKing);
        }
      }, 150);
      // Give up quietly after 15s — buttons stay in their default (empty) state.
      setTimeout(() => clearInterval(id), 15000);
    });
  }

  /** Subscribe to the live wishlist; every emission re-syncs all UI. */
  #subscribe(wk) {
    try {
      const observable = wk.state.observeWishlist({ wishlistId: 'mine' });
      observable.subscribe((wishlist) => this.#applyWishlist(wishlist));
    } catch (error) {
      console.error('[wishlist-toggle] failed to observe wishlist', error);
    }
  }

  /** Rebuild the productId -> itemId map from a wishlist snapshot and re-render. */
  #applyWishlist(wishlist) {
    const next = new Map();
    for (const item of wishlist?.items ?? []) {
      const productId = item.product?.id ?? item.productId;
      if (productId != null) next.set(String(productId), item.id);
    }
    this.#itemsByProductId = next;
    this.#ready = true;
    for (const button of this.#buttons) button.syncFromController(this);
    this.#updateHeaderCount(wishlist?.numItems ?? next.size);
  }

  register(button) {
    this.#buttons.add(button);
    if (this.#ready) button.syncFromController(this);
  }

  unregister(button) {
    this.#buttons.delete(button);
  }

  /** True when the given product is in the wishlist. */
  isWishlisted(productId) {
    return this.#itemsByProductId.has(String(productId));
  }

  /** wishlistItemId for a product, or undefined. */
  itemId(productId) {
    return this.#itemsByProductId.get(String(productId));
  }

  /** Reflects the current item count into the header "Favourites (N)" badge. */
  #updateHeaderCount(count) {
    this.#lastCount = count;
    this.syncHeaderCount();
  }

  /**
   * Writes the last known count into any header badge currently in the DOM.
   * Called both on each wishlist emission and when a header badge mounts late
   * (e.g. the /apps/wishlist app-proxy page renders its header after our first
   * emission), so the counter never gets stuck at its server-rendered 0.
   */
  syncHeaderCount() {
    for (const el of document.querySelectorAll('.header-actions__wishlist-count')) {
      el.textContent = String(this.#lastCount);
    }
  }
}

/**
 * Wraps a single `[data-wishlist-toggle]` <button>, wiring its click to Swish
 * and reflecting wishlist state back onto it. The button stays a plain element
 * (it cannot be a custom element), so we upgrade it imperatively and stash this
 * wrapper on the node to avoid double-binding.
 */
class WishlistToggle {
  static upgrade(button) {
    if (button.__wishlistToggle) return button.__wishlistToggle;
    return (button.__wishlistToggle = new WishlistToggle(button));
  }

  constructor(button) {
    this.button = button;
    button.addEventListener('click', this.#handleClick.bind(this));
    WishlistController.get().register(this);
  }

  get productId() {
    return this.button.dataset.productId;
  }

  /** Reflects the controller's current state onto this button. */
  syncFromController(controller) {
    this.#setSelected(controller.isWishlisted(this.productId));
  }

  #setSelected(selected) {
    this.button.setAttribute('aria-pressed', String(selected));
    this.button.classList.toggle('is-wishlisted', selected);
  }

  async #handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const wk = window.WishlistKing;
    if (!wk?.apis?.wishlist) return;

    const controller = WishlistController.get();
    const wasSelected = controller.isWishlisted(this.productId);

    // Optimistic flip; the live subscription re-syncs from the real state once
    // the request resolves (and reverts here if it throws).
    this.#setSelected(!wasSelected);
    this.button.toggleAttribute('disabled', true);

    try {
      // Use the low-level wishlist API directly: the high-level
      // add/removeWishlistItem helpers resolve the handle -> productId server
      // side (which is unreliable for guest sessions) and early-return while
      // another action is in flight. We already have the productId on the button.
      if (wasSelected) {
        const itemId = controller.itemId(this.productId);
        if (itemId != null) {
          await wk.apis.wishlist.removeItem({ wishlistId: 'mine', wishlistItemId: itemId });
        }
      } else {
        const response = await wk.apis.wishlist.addItem({
          productId: parseInt(this.productId, 10),
          variantId: parseInt(this.button.dataset.variantId, 10),
        });
        // The low-level addItem only keeps the guest sessionId in memory (the
        // high-level flow persists it via the `app:init-sid` event). Persist it
        // ourselves, or the wishlist is orphaned on the next navigation and
        // /apps/wishlist renders empty for guests.
        if (response?.sessionId) wk.setSessionId?.(response.sessionId);
      }
      // Push Swish to re-fetch so its observable (and thus every button + the
      // counter) reflects the new server state.
      await wk.state.revalidate(true);
    } catch (error) {
      console.error('[wishlist-toggle] toggle failed', error);
      this.#setSelected(wasSelected); // revert on failure
    } finally {
      this.button.toggleAttribute('disabled', false);
    }
  }
}

function upgradeAll(root = document) {
  for (const button of root.querySelectorAll('[data-wishlist-toggle]')) {
    WishlistToggle.upgrade(button);
  }
}

// Start the controller on every page — the header "Favourites (N)" badge needs
// the live count even on pages with no product cards (the wishlist page itself,
// cart, account, etc.), so we can't wait for a button to lazily create it.
WishlistController.get();

// Upgrade existing buttons, then catch any added later (lazy-loaded grids,
// section re-renders, infinite scroll).
upgradeAll();

new MutationObserver((mutations) => {
  let headerBadgeAdded = false;
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      if (node.matches?.('[data-wishlist-toggle]')) WishlistToggle.upgrade(node);
      else upgradeAll(node);
      // A header count badge can mount after our first wishlist emission (the
      // app-proxy wishlist page renders its header late); re-apply the count.
      if (
        node.matches?.('.header-actions__wishlist-count') ||
        node.querySelector?.('.header-actions__wishlist-count')
      ) {
        headerBadgeAdded = true;
      }
    }
  }
  if (headerBadgeAdded) WishlistController.get().syncHeaderCount();
}).observe(document.documentElement, { childList: true, subtree: true });
